/**
 * Phase 10.1 — Knowledge Graph visualizer drawer.
 *
 * Read-only. Fetches `/api/v1/kg/nodes/{nodeId}/neighborhood?depth=2`
 * on open and renders the returned nodes/edges via react-flow. No
 * layout library dependency (we ship a lightweight radial layout to
 * keep bundle size small — dagre/elkjs deferred as backlog).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import ReactFlow, {
  Background, Controls, useReactFlow, ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";

const NODE_COLORS = {
  Campaign:     "bg-indigo-100 border-indigo-500 text-indigo-900",
  Creative:     "bg-purple-100 border-purple-500 text-purple-900",
  LandingPage:  "bg-emerald-100 border-emerald-500 text-emerald-900",
  Experiment:   "bg-amber-100 border-amber-500 text-amber-900",
  Outcome:      "bg-teal-100 border-teal-500 text-teal-900",
  ContentPost:  "bg-sky-100 border-sky-500 text-sky-900",
  Region:       "bg-slate-100 border-slate-500 text-slate-900",
  Audience:     "bg-rose-100 border-rose-500 text-rose-900",
  Channel:      "bg-fuchsia-100 border-fuchsia-500 text-fuchsia-900",
  default:      "bg-slate-100 border-slate-400 text-slate-800",
};

export function nodeColorClass(nodeType) {
  return NODE_COLORS[nodeType] || NODE_COLORS.default;
}

// Radial layout: root at centre, first shell at r=200, second at r=400.
function radialLayout(nodes, edges, rootId) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const depths = { [rootId]: 0 };
  const queue = [rootId];
  while (queue.length) {
    const cur = queue.shift();
    edges.forEach((e) => {
      const [a, b] = [e.source_node_id, e.target_node_id];
      const nxt = a === cur ? b : b === cur ? a : null;
      if (nxt && depths[nxt] === undefined && byId[nxt]) {
        depths[nxt] = depths[cur] + 1;
        queue.push(nxt);
      }
    });
  }
  const shells = {};
  Object.entries(depths).forEach(([id, d]) => {
    (shells[d] = shells[d] || []).push(id);
  });
  const positions = {};
  Object.entries(shells).forEach(([d, ids]) => {
    const r = Number(d) * 200;
    const step = (2 * Math.PI) / Math.max(1, ids.length);
    ids.forEach((id, i) => {
      positions[id] = r === 0
        ? { x: 0, y: 0 }
        : { x: Math.cos(step * i) * r, y: Math.sin(step * i) * r };
    });
  });
  return positions;
}

function KGCanvas({ data }) {
  const { fitView } = useReactFlow();
  const positions = useMemo(
    () => radialLayout(data.nodes, data.edges, data.root_id),
    [data]);
  const rfNodes = data.nodes.map((n) => ({
    id: n.id,
    position: positions[n.id] || { x: 0, y: 0 },
    data: {
      label: (
        <div className={"px-3 py-2 rounded-md border-2 text-xs " + nodeColorClass(n.node_type)}>
          <div className="font-semibold">{n.node_type}</div>
          <div className="truncate max-w-[160px]">{n.label}</div>
        </div>
      ),
    },
    style: { background: "transparent", border: "none", padding: 0 },
  }));
  const rfEdges = data.edges.map((e) => ({
    id: e.id, source: e.source_node_id, target: e.target_node_id,
    label: e.edge_type,
    labelStyle: { fontSize: 10, fill: "#475569" },
    labelBgStyle: { fill: "white", fillOpacity: 0.85 },
    animated: false,
  }));
  return (
    <div data-testid="kg-canvas" style={{ width: "100%", height: "60vh" }}>
      <ReactFlow nodes={rfNodes} edges={rfEdges} fitView minZoom={0.2}>
        <Background/>
        <Controls showInteractive={false}/>
      </ReactFlow>
      <div className="mt-2 flex justify-end">
        <Button size="sm" variant="outline"
                data-testid="kg-reset-view"
                onClick={() => fitView({ duration: 300 })}>
          Reset view
        </Button>
      </div>
    </div>
  );
}

export default function KnowledgeGraphDrawer({ nodeId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!nodeId) return;
    setLoading(true); setError(null);
    try {
      const r = await api.get(
        `/kg/nodes/${nodeId}/neighborhood?depth=2`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail?.error || "load_failed");
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  useEffect(() => { if (open && nodeId) load(); }, [open, nodeId, load]);

  const isEmpty = data && (data.nodes || []).length <= 1
                  && (data.edges || []).length === 0;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <SheetContent side="right" className="sm:max-w-3xl w-full"
                    data-testid="kg-drawer">
        <SheetHeader>
          <SheetTitle>Knowledge Graph</SheetTitle>
          <SheetDescription>
            Neighborhood (depth 2) around this entity.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4">
          {loading && (
            <div data-testid="kg-loading"
                 className="animate-pulse text-sm text-muted-foreground">
              Loading graph…
            </div>
          )}
          {error && (
            <div data-testid="kg-error" className="text-sm text-red-600">
              Failed to load: {error}
            </div>
          )}
          {!loading && !error && isEmpty && (
            <div data-testid="kg-empty"
                 className="text-sm text-muted-foreground py-8 text-center">
              No graph data yet — try republishing this campaign to
              backfill events.
            </div>
          )}
          {!loading && !error && data && !isEmpty && (
            <ReactFlowProvider>
              <KGCanvas data={data}/>
            </ReactFlowProvider>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
